import { OrderOwnershipKind } from './strategy-ledger';

export type OrderSide = 'BUY' | 'SELL';
export type OrderSource = 'DEMETER' | 'EXTERNAL';
export type OrderType =
	| 'MARKET'
	| 'MARKET_ON_CLOSE'
	| 'STOP'
	| 'LIMIT'
	| 'STOP_LIMIT'
	| 'TRAILING_STOP'
	| 'UNKNOWN';
export type TimeInForce =
	| 'DAY'
	| 'GOOD_UNTIL_CANCELED'
	| 'OPG'
	| 'IMMEDIATE_OR_CANCEL'
	| 'IOC_NO_PARTIAL'
	| 'MARKET_CLOSE'
	| 'GTX'
	| 'UNKNOWN';
export type OrderStatus =
	| 'NEW'
	| 'PARTIALLY_FILLED'
	| 'FILLED'
	| 'CANCELED'
	| 'REJECTED'
	| 'PENDING'
	| 'EXPIRED'
	| 'ACCEPTED'
	| 'UNKNOWN';

// Common's DebitCredit: a combo's net price direction. NONE is the wire default ("no direction
// known") and prints no suffix — read through netSuffix()/netWord() in src/lib/utils/combo.ts.
export type DebitCredit = 'NONE' | 'EVEN' | 'DEBIT' | 'CREDIT';
export type PositionEffect = 'OPEN' | 'CLOSE';

// One leg of a COMBO_SPREAD order (Common OrderLeg). All five members are always on the wire.
// `side` is the leg's own buy/sell — ABSOLUTE, never relative to the package side. Fills are
// combo-level only: a leg carries no fill, price or status, and none is ever rendered per leg.
export interface OrderLeg {
	symbol: string;
	side: OrderSide;
	ratio: number;
	securityType: string; // 'OPTION' | 'FUTURES_OPTION' | 'STOCK' | …
	positionEffect: PositionEffect;
}

export interface OrderRequest {
	symbol: string;
	qty: number;
	orderType: OrderType;
	price: number;
	stopPrice?: number;
	timeInForce: TimeInForce;
	side: OrderSide;
	id?: string;
	isExtendedHours?: boolean;
	// Deliberate subset of Common's SecurityType (FUTURE / CONTRACT_FOR_DIFFERENCE exist server-side);
	// absent means STOCK, which is how futures orders ride today. COMBO_SPREAD is read-only here
	// (Core.Trader's combo steps place it); the order form never sends it. securityType on a read
	// may carry any Common member; never switch on it exhaustively.
	securityType?: 'STOCK' | 'OPTION' | 'COMBO_SPREAD';
	positionEffect?: PositionEffect;
	// COMBO_SPREAD only: the legs and the net price's direction. orderRequest.legs/debitCredit are
	// always present on the read path ([] / 'NONE' for a non-combo order); read through
	// hasLegs()/netSuffix(); the order form never sends either.
	legs?: OrderLeg[];
	debitCredit?: DebitCredit;
	// The trader whose share the fills post to (Core.Trader#265). null/absent = unowned ("None").
	strategyId?: string | null;
}

export interface OrderDTO {
	orderId: string;
	orderStatus: OrderStatus;
	orderType: OrderType;
	orderSide: OrderSide;
	timeInForce: TimeInForce;
	qty?: number;
	symbol?: string;
	averageFillPrice?: number;
	stopPrice?: number;
	limitPrice?: number;
	filledQty?: number;
}

// Common's OrderEventKind on the wire. Only STATUS (or an absent kind) is a status change; the
// three MODIFY kinds are change rows (Trade.Eze#81: MODIFY_REJECTED / MODIFY_UNCONFIRMED are
// recorded, never projected). Unknown strings are tolerated and render as a neutral status row.
export type OrderEventKind =
	| 'STATUS'
	| 'MODIFY'
	| 'MODIFY_REJECTED'
	| 'MODIFY_UNCONFIRMED'
	| string;

export interface OrderEvent {
	// Absent ⇒ STATUS (rows written before the modify-events deploy).
	kind?: OrderEventKind;
	status: OrderStatus;
	timestamp: string; // ISO
	filledQty?: number;
	averageFillPrice?: number;
	reason?: string;
}

export interface OrderSummaryDTO {
	orderId: string;
	accountId: string;
	fundId?: string;
	brokerId: string;
	orderRequest: OrderRequest;
	order?: OrderDTO;
	createdAt: string;
	// DEMETER (originated by Demeter) or EXTERNAL (placed directly at the broker, e.g. an Eze desk
	// trade). EXTERNAL orders carry a stub orderRequest (blank symbol, qty 0), so render the
	// Symbol/Side/Qty/Type columns from `order` and badge the row. Optional/absent ⇒ treat as DEMETER.
	source?: OrderSource;
	// Lifecycle events, ascending by (whole-second timestamp, lifecycle rank, |filledQty|, audit
	// row id) — Core.API orders them with the same key it uses to project the order's current
	// status, so the timeline and the row always agree. The id is Core.API-side only and is not
	// exposed on OrderEvent, so this order cannot be reproduced client-side; render as received.
	//
	// Consequence: within one second the raw timestamps can read out of numeric order (a PENDING
	// at .024 above a FILLED at .000), which is correct by lifecycle. Both display formats below
	// hide sub-second, so it is invisible — see the format site in
	// order-detail-modal.component.html before widening either one.
	//
	// Timestamps are NOT sub-second comparable, and how each broker's are produced differs:
	//   ALPACA   broker envelope time, but at DIFFERENT PRECISION PER EVENT TYPE — pending_new in
	//            milliseconds, fill commonly truncated to the whole second. Least comparable in
	//            the fleet, and the root cause of Core.API#116.
	//   EZE, IB  stamped by OUR poller when it noticed (may lag ~30s), not broker event time.
	//   TRAFIX   push (SDK callbacks); stamped when our handler processed the broker's push.
	//   ADMIS    push (FIX ExecutionReports); same caveat. Whether either carries the broker's
	//            own field is per-adapter and unverified — do not assume broker event time.
	//   MOCK     stamps its own UtcNow at publish.
	// Never order or diff these below the second, on any broker.
	//
	// Populated by GetOrder?includeEvents=true; absent on list rows from GetOrders.
	events?: OrderEvent[];
	// Set only when this order was one of SEVERAL broker sub-orders produced by a single
	// placement (e.g. an EZE futures order split across the per-order contract cap). Null/absent
	// for the common single-order-per-placement case — do not render a badge for those rows.
	placementGroupId?: string;
	strategyId?: string | null;
	// The execution plan that placed this order (Core.API#147); null/absent = not a plan order.
	executionPlanId?: string | null;
	// The live ownership claim (order_attributions; Core.Trader#265). All null after a reversal.
	// Adoption is not a kind: it is adoptedPlanId being non-null.
	ownershipKind?: OrderOwnershipKind | string | null;
	ownedByStrategyId?: string | null;
	ownershipEntryId?: number | null;
	ownedAt?: string | null;
	// null for AUTO (a system entry).
	ownedBy?: string | null;
	adoptedPlanId?: string | null;
	adoptedPlanLabel?: string | null;
	adoptionId?: string | null;
}

// Response element from POST /funds/{accountId}/orders — one per submitted OrderRequest, in
// request order. `orders` lists the broker sub-order(s) the placement produced.
export interface OrderPlacement {
	orders: { orderId: string; qty: number }[];
	error?: string;
}

export const CANCELABLE_STATUSES: OrderStatus[] = [
	'NEW',
	'PENDING',
	'ACCEPTED',
	'PARTIALLY_FILLED'
];

export interface PaginatedResponse<T> {
	items: T[];
	page: number;
	pageSize: number;
	totalCount: number;
	totalPages: number;
	unfilteredTotalCount?: number;
}

// Core.API#147 grouped Order History. Working = Core.API OpenStatuses (incl. UNKNOWN); untracked =
// no status yet or an unrecognised one. The six always sum to orderCount.
export interface OrderStatusCounts {
	working: number;
	rejected: number;
	canceled: number;
	expired: number;
	filled: number;
	untracked: number;
}

// Every order one execution plan placed for one symbol and side. Figures cover ALL members, never
// only the filter-matching ones; shared terms are null when members differ.
export interface OrderGroup {
	planId: string;
	symbol: string;
	side: OrderSide;
	strategyId?: string | null;
	brokerId?: string | null;
	accountId?: string | null;
	partCount: number;
	orderCount: number;
	qty: number;
	filledQty: number;
	avgFillPrice?: number | null;
	firstCreatedAt: string;
	lastCreatedAt: string;
	statusCounts: OrderStatusCounts;
	orderType?: OrderType | null;
	limitPrice?: number | null;
	stopPrice?: number | null;
	// Combo identity (UI#262; absent on an older Core.API). securityType is the members' shared
	// Common SecurityType — null when they differ or any is legacy NULL. legs/debitCredit are set
	// only when the group's lowest- and highest-id members agree (a cheap check, not exhaustive);
	// legsVary = those two differ, and legs is then null. comboEffect is the members' open/close,
	// known even when legsVary.
	securityType?: string | null;
	legs?: OrderLeg[] | null;
	debitCredit?: DebitCredit | null;
	legsVary?: boolean;
	comboEffect?: PositionEffect | null;
}

// One row of GET /funds/{id}/orders?groupBy=plan (exactly one of order / group), or one member of
// the member list (order + matchesFilter).
export interface OrderListItem {
	order?: OrderSummaryDTO | null;
	group?: OrderGroup | null;
	matchesFilter?: boolean | null;
}

export const ORDER_STRATEGY_NONE = 'none';

export interface OrderQueryParams {
	brokerId?: string;
	brokerAccountId?: string;
	enrich?: boolean;
	page?: number;
	pageSize?: number;
	sortBy?: string;
	sortDirection?: 'asc' | 'desc';
	symbol?: string;
	status?: string;
	side?: string;
	strategyId?: string;
	createdFrom?: string;
	createdTo?: string;
}
